import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { Button } from '@mui/material';
import ThemeProvider from '@shared/theme/ThemeProvider';
import { QueryClientProvider } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router';
import AppStartup from '@/component/AppStartup/AppStartup';
import { ErrorBoundary } from '@/component/Error';
import { ToastContainer } from '@/component/Toast';
import Translate from '@/component/Translate';
import AccountProvider from '@/feature/account/AccountProvider';
import PrefetchData from '@/feature/app/PrefetchData';
import AuthProvider from '@/feature/auth/AuthProvider';
import keycloak from '@/feature/auth/keycloak';
import { BuildInfo } from '@/feature/build';
import { CartProvider } from '@/feature/cart';
import { DataStreamManager } from '@/feature/dataStream/Manager';
import { features, FeatureToggle } from '@/feature/features';
import { reactPlugin } from '@/feature/insights/appInsights';
import { useTexts } from '@/feature/lang';
import LangProvider from '@/feature/lang/context';
import { getDefaultLanguage } from '@/feature/lang/util';
import { PermissionsProvider } from '@/feature/permissions/PermissionsProvider';
import queryClient from '@/feature/query/queryClient';
import { initSentry } from '@/feature/sentry';
import { initGA } from '@/feature/tracking';
import '@microsoft/applicationinsights-shims';
import 'normalize.css';
import App from './App';

initGA();
initSentry();

const container = document.getElementById('root');
const root = createRoot(container!);
root.render(
  <AuthProvider keycloak={keycloak}>
    <StrictMode>
      <AppInsightsContext.Provider value={reactPlugin}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <Router>
              <LangProvider defaultLanguage={getDefaultLanguage()}>
                <ErrorBoundaryWithAction>
                  <ToastContainer>
                    <AppStartup>
                      <PermissionsProvider>
                        <FeatureToggle features={features}>
                          <CartProvider>
                            <AccountProvider>
                              <App />
                            </AccountProvider>
                            <PrefetchData />
                          </CartProvider>
                        </FeatureToggle>
                      </PermissionsProvider>
                      <DataStreamManager />
                    </AppStartup>
                  </ToastContainer>
                </ErrorBoundaryWithAction>
              </LangProvider>
            </Router>
          </ThemeProvider>
          <BuildInfo />
        </QueryClientProvider>
      </AppInsightsContext.Provider>
    </StrictMode>
  </AuthProvider>
);

interface ErrorBoundaryWithActionProps {
  children: ReactElement;
}
function ErrorBoundaryWithAction({ children }: ErrorBoundaryWithActionProps) {
  const { app } = useTexts();

  return (
    <ErrorBoundary
      action={
        <Button href="/" variant="outlined">
          <Translate text={app.navigation.home.label} />
        </Button>
      }
    >
      {children}
    </ErrorBoundary>
  );
}
